/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { CacheType, createObservableDataAction, getCatalogId, IAction, IActionContext, IActionInput } from '@msdyn365-commerce/core';
import { ProductPrice, ProductsDataActions, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';

import { ArrayExtensions } from './utilities';
import { QueryResultSettingsProxy } from './utilities/QueryResultSettingsProxy';

/**
 * Input class for the GetPriceForProductSearchResult Data Action.
 */
export class ProductPriceForSelectedProductsInput implements IActionInput {
    public productIds: number[];

    public constructor(products: number[]) {
        this.productIds = products;
    }

    /**
     * Get the cache key.
     * @returns The cache key.
     */
    public getCacheKey = (): string => `ProductPriceForSelectedProducts-${JSON.stringify(this.productIds)}`;

    /**
     * Get the cache object type.
     * @returns The cache object type.
     */
    public getCacheObjectType = (): string => 'ProductPrices';

    /**
     * Define the data cache type.
     * @returns The cache type.
     */
    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the GetPriceForProductSearchResultDataAction.
 * @param products -- The product search result.
 * @returns The input.
 */
export const createProductPriceForSelectedProductsInput = (productIds: number[]): ProductPriceForSelectedProductsInput => {
    return new ProductPriceForSelectedProductsInput(productIds);
};

/**
 * The Action Method for the getProductPriceForSelectedProducts Data Action
 * Gets the active price for selected products via the getActivePrice RetailServer API.
 * @param input -- The data input.
 * @param context -- The action context.
 * @returns The Product Prices.
 */
export async function getProductPriceForSelectedProductsAction(
    input: ProductPriceForSelectedProductsInput,
    context: IActionContext
): Promise<ProductPrice[]> {
    let productRecIds: number[];
    if (ArrayExtensions.hasElements(input.productIds)) {
        productRecIds = input.productIds;
    } else {
        return Promise.resolve([]);
    }

    const catalogId = getCatalogId(context.requestContext);

    const projectDomain: ProjectionDomain = { ChannelId: +context.requestContext.apiSettings.channelId, CatalogId: catalogId };

    try {
        const productPrices: ProductPrice[] = await ProductsDataActions.getActivePricesAsync(
            { callerContext: context, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(context) },
            projectDomain,
            productRecIds,
            new Date(),
            null,
            undefined,
            true
        );

        return Promise.resolve(productPrices);
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- existing error type.
        context.telemetry.exception(error);
        context.telemetry.debug('[getProductPriceForSelectedProductsAction]Error executing action');
        throw new Error('[getProductPriceForSelectedProductsAction]Error executing action');
    }
}

/**
 * The getProductPriceForSelectedProducts data action.
 */
export const getProductPriceForSelectedProductsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-prices-for-selected-products',
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- existing pattern.
    action: <IAction<ProductPrice[]>>getProductPriceForSelectedProductsAction,
    input: createProductPriceForSelectedProductsInput
});

export default getProductPriceForSelectedProductsDataAction;
